import {
  Between, Equal, GreaterThan, IQueryCommandOptions, Not, Null, convertQueryCommandToQueryString,
} from '@llws/dynamic-router-utils';
import { REACT_APP_API_ROOT_URI } from '../../../App';
import { dispatch } from '../../store';
import { IApiQueryListResponse, typedApiFetch } from '../../utils';
import { makeYmd } from '../../../app-utils';
import { Invoice } from '../../../../../entities/hydra';
import { IInvoiceStatusCounts, invoiceStatusCountsReceived, setLoadingState } from './dashboardSlice';

/**
 * Returns the first and last day of the calendar month that is `monthsAgo` months before today.
 * `monthsAgo = 0` → current month, `monthsAgo = 1` → last month, etc.
 */
const getMonthBounds = (monthsAgo: number): { start: Date; end: Date } => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth() - monthsAgo, 1);
  const end = new Date(today.getFullYear(), today.getMonth() - monthsAgo + 1, 0);
  return { start, end };
};

/**
 * Fetches the `totalRecords` count from the invoices dynamic-router endpoint using the given filter options.
 * Uses `limit: 1` to avoid pulling back full record payloads — only the `meta.totalRecords` field is used.
 */
const getInvoiceCount = async (options: IQueryCommandOptions): Promise<number> => {
  const url = `${REACT_APP_API_ROOT_URI}/v1/hydra/invoices${convertQueryCommandToQueryString(options)}`;
  const response = await typedApiFetch<IApiQueryListResponse<Invoice>>(url, { method: 'GET' });
  const responseData = await response.json();
  return responseData.meta.totalRecords;
};

/**
 * Loads non-deleted, non-zero-dollar invoice counts for the given month, bucketed by status, into the
 * dashboard slice. `monthsAgo` is `0` for the current month, `1` for last month, etc. Queries the dynamic
 * router directly (bypassing the shared `dynamicHydra` slice) since several concurrent count queries against the same
 * `invoices` resource would otherwise race and clobber that slice's single global list/meta slot.
 */
export const loadInvoiceStatusCounts = async (monthsAgo: number): Promise<void> => {
  dispatch(setLoadingState('loading'));

  const { start, end } = getMonthBounds(monthsAgo);
  const dateRange = Between('i.invoiceDate', makeYmd(start), makeYmd(end));
  const hasAmountInvoiced = GreaterThan('i.amountInvoiced', 0);

  const [total, draft, published, sent] = await Promise.all([
    getInvoiceCount({
      alias: 'i',
      limit: 1,
      where: [dateRange, hasAmountInvoiced, Not(Equal('i.hydraState', 'deleted'))],
    }),
    getInvoiceCount({
      alias: 'i',
      limit: 1,
      where: [dateRange, hasAmountInvoiced, Equal('i.hydraState', 'unsent'), Null('i.freshbooksState')],
    }),
    getInvoiceCount({
      alias: 'i',
      limit: 1,
      where: [dateRange, hasAmountInvoiced, Equal('i.hydraState', 'sent'), Equal('i.freshbooksState', 'draft')],
    }),
    getInvoiceCount({
      alias: 'i',
      limit: 1,
      where: [
        dateRange,
        hasAmountInvoiced,
        Equal('i.hydraState', 'sent'),
        Not(Equal('i.freshbooksState', 'draft')),
        Not(Null('i.freshbooksState')),
      ],
    }),
  ]);

  const counts: IInvoiceStatusCounts = { total, draft, published, sent };
  dispatch(invoiceStatusCountsReceived({ monthsAgo, counts }));
};
